import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import {
  Loader2, Sparkles, Users, Clock, CheckCircle2, ExternalLink,
  ShieldCheck, X, RefreshCw, Send,
} from "lucide-react";
import { adminGet, getToken } from "@/lib/adminApi";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}
function daysUntil(iso) {
  if (!iso) return null;
  try {
    const diff = new Date(iso) - new Date();
    return Math.max(0, Math.round(diff / (1000 * 60 * 60 * 24)));
  } catch { return null; }
}

export default function PremiumCoursesTab() {
  const [tab, setTab] = useState("intents"); // intents | accesses | courses
  const [loading, setLoading] = useState(true);
  const [intents, setIntents] = useState([]);
  const [accesses, setAccesses] = useState([]);
  const [courses, setCourses] = useState([]);
  const [granting, setGranting] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [i, a, c] = await Promise.all([
        adminGet("/admin/course-purchase-intents"),
        adminGet("/admin/course-access"),
        adminGet("/admin/courses"),
      ]);
      setIntents(i.items || []);
      setAccesses(a.items || []);
      setCourses((c.items || []).filter((cc) => cc.is_premium));
    } catch {
      toast.error("Errore caricamento");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const grant = async (email, course_slug, days = null) => {
    const key = `${email}::${course_slug}`;
    setGranting((s) => ({ ...s, [key]: true }));
    try {
      await axios.post(
        `${API}/admin/course-access/grant`,
        { email, course_slug, days },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      toast.success(`Accesso attivato per ${email}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore attivazione accesso");
    } finally {
      setGranting((s) => ({ ...s, [key]: false }));
    }
  };

  const revoke = async (email, course_slug) => {
    if (!confirm(`Revocare accesso a ${email} per ${course_slug}?`)) return;
    try {
      await axios.post(
        `${API}/admin/course-access/revoke`,
        { email, course_slug },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      toast.success("Accesso revocato");
      load();
    } catch { toast.error("Errore revoca"); }
  };

  if (loading) return <div className="p-10 text-center"><Loader2 className="animate-spin inline" /></div>;

  const pendingIntents = intents.filter((i) => i.status === "pending");
  const doneIntents = intents.filter((i) => i.status !== "pending");
  const activeAccesses = accesses.filter((a) => !a.expires_at || new Date(a.expires_at) > new Date());
  const courseTitleBySlug = Object.fromEntries(courses.map((c) => [c.slug, c.title]));

  return (
    <div className="space-y-6" data-testid="admin-premium-courses-tab">
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <TabBtn active={tab === "intents"} onClick={() => setTab("intents")} testid="pctab-intents">
            <Sparkles size={14} /> Richieste ({pendingIntents.length})
          </TabBtn>
          <TabBtn active={tab === "accesses"} onClick={() => setTab("accesses")} testid="pctab-accesses">
            <ShieldCheck size={14} /> Accessi attivi ({activeAccesses.length})
          </TabBtn>
          <TabBtn active={tab === "courses"} onClick={() => setTab("courses")} testid="pctab-courses">
            <Users size={14} /> Corsi ({courses.length})
          </TabBtn>
        </div>
        <button onClick={load} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-[#0E7C8F]">
          <RefreshCw size={14} /> Aggiorna
        </button>
      </div>

      {tab === "intents" && (
        <div className="space-y-6">
          {/* Pending */}
          <section>
            <h3 className="font-serif-display text-xl text-[#0B192C] mb-3">In attesa di attivazione ({pendingIntents.length})</h3>
            {pendingIntents.length === 0 ? (
              <p className="text-sm text-[#5A6472] p-6 bg-white border border-[#E5E1DA]">Nessuna richiesta in sospeso.</p>
            ) : (
              <div className="space-y-3">
                {pendingIntents.map((i) => {
                  const key = `${i.email}::${i.course_slug}`;
                  return (
                    <div key={i.id} className="bg-white border border-[#E5E1DA] p-5 flex items-start justify-between gap-4" data-testid={`intent-${i.id}`}>
                      <div className="flex-1">
                        <p className="font-semibold text-[#0B192C]">{i.course_title}</p>
                        <p className="text-sm text-[#5A6472]">
                          {i.name || "-"} · <a href={`mailto:${i.email}`} className="text-[#0E7C8F]">{i.email}</a>
                        </p>
                        <p className="text-xs text-[#5A6472] mt-1">
                          <Clock size={12} className="inline mr-1" /> Richiesta: {fmtDate(i.created_at)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-mono font-bold text-[#0B192C]">€ {Number(i.price).toFixed(2)}</p>
                        <div className="flex gap-2 mt-3">
                          <button
                            onClick={() => grant(i.email, i.course_slug)}
                            disabled={granting[key]}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-[#0B7C5C] text-white text-xs uppercase tracking-[0.15em] hover:bg-[#0B192C] disabled:opacity-60"
                            data-testid={`grant-${i.id}`}
                          >
                            {granting[key] ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
                            Attiva accesso
                          </button>
                        </div>
                        <p className="text-[10px] text-[#5A6472] mt-2">
                          Genera link Stripe manualmente e invia via email
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* Done */}
          {doneIntents.length > 0 && (
            <section>
              <h3 className="font-serif-display text-lg text-[#0B192C] mb-3">Cronologia richieste evase ({doneIntents.length})</h3>
              <div className="bg-white border border-[#E5E1DA]">
                <table className="w-full text-sm">
                  <thead className="bg-[#F9F8F6] text-[10px] uppercase text-[#5A6472]">
                    <tr>
                      <th className="text-left px-4 py-2">Corso</th>
                      <th className="text-left px-4 py-2">Iscritto</th>
                      <th className="text-right px-4 py-2">Prezzo</th>
                      <th className="text-center px-4 py-2">Stato</th>
                      <th className="text-left px-4 py-2">Data</th>
                    </tr>
                  </thead>
                  <tbody>
                    {doneIntents.map((i) => (
                      <tr key={i.id} className="border-t border-[#E5E1DA]">
                        <td className="px-4 py-2 font-semibold text-[#0B192C]">{i.course_title}</td>
                        <td className="px-4 py-2 text-xs">{i.email}</td>
                        <td className="px-4 py-2 text-right font-mono">€ {Number(i.price).toFixed(2)}</td>
                        <td className="px-4 py-2 text-center">
                          <span className="px-2 py-0.5 text-[10px] uppercase bg-[#0B7C5C] text-white">
                            {i.status}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-xs text-[#5A6472]">{fmtDate(i.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </div>
      )}

      {tab === "accesses" && (
        <section>
          <h3 className="font-serif-display text-xl text-[#0B192C] mb-3">Accessi attivi ({activeAccesses.length})</h3>
          {activeAccesses.length === 0 ? (
            <p className="text-sm text-[#5A6472] p-6 bg-white border border-[#E5E1DA]">Nessun accesso premium attivo.</p>
          ) : (
            <div className="bg-white border border-[#E5E1DA]">
              <table className="w-full text-sm">
                <thead className="bg-[#0B192C] text-[#F9F8F6] text-[10px] uppercase tracking-[0.15em]">
                  <tr>
                    <th className="text-left px-4 py-3">Email</th>
                    <th className="text-left px-4 py-3">Corso</th>
                    <th className="text-left px-4 py-3">Attivato</th>
                    <th className="text-left px-4 py-3">Scade</th>
                    <th className="text-center px-4 py-3">Giorni rimasti</th>
                    <th className="text-center px-4 py-3">Origine</th>
                    <th className="w-20"></th>
                  </tr>
                </thead>
                <tbody>
                  {activeAccesses.map((a, idx) => {
                    const days = daysUntil(a.expires_at);
                    return (
                      <tr key={idx} className="border-t border-[#E5E1DA]" data-testid={`access-${a.email}-${a.course_slug}`}>
                        <td className="px-4 py-2 text-xs">{a.email}</td>
                        <td className="px-4 py-2 font-semibold text-[#0B192C]">
                          {courseTitleBySlug[a.course_slug] || a.course_slug}
                        </td>
                        <td className="px-4 py-2 text-xs text-[#5A6472]">{fmtDate(a.granted_at)}</td>
                        <td className="px-4 py-2 text-xs text-[#5A6472]">{fmtDate(a.expires_at)}</td>
                        <td className="px-4 py-2 text-center">
                          <span className={`px-2 py-1 text-xs font-mono ${days > 60 ? "bg-[#0B7C5C]" : days > 15 ? "bg-[#E89B6C]" : "bg-[#B23A48]"} text-white`}>
                            {days}g
                          </span>
                        </td>
                        <td className="px-4 py-2 text-center text-xs">
                          <span className="px-2 py-0.5 bg-[#F4EDE0] text-[#0B192C]">
                            {(a.granted_by || "").includes("stripe") ? "Stripe" : "Admin"}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-center">
                          <button
                            onClick={() => revoke(a.email, a.course_slug)}
                            className="p-1.5 border border-[#B23A48] text-[#B23A48] hover:bg-[#B23A48] hover:text-white"
                            title="Revoca accesso"
                            data-testid={`revoke-${a.email}-${a.course_slug}`}
                          >
                            <X size={12} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {tab === "courses" && (
        <section>
          <h3 className="font-serif-display text-xl text-[#0B192C] mb-3">Catalogo corsi Premium ({courses.length})</h3>
          <div className="grid md:grid-cols-2 gap-4">
            {courses.map((c) => {
              const accessCount = activeAccesses.filter((a) => a.course_slug === c.slug).length;
              const revenue = accessCount * (c.price || 0);
              return (
                <div key={c.slug} className="bg-white border border-[#E5E1DA] p-5" data-testid={`admin-premium-course-${c.slug}`}>
                  <div className="flex items-start gap-4">
                    {c.cover_image && (
                      <img src={c.cover_image} alt={c.title} className="w-24 h-16 object-cover" />
                    )}
                    <div className="flex-1">
                      <p className="font-semibold text-[#0B192C]">{c.title}</p>
                      <p className="text-xs text-[#5A6472] mt-1">
                        {c.modules_count} moduli · {c.lessons_count} lezioni · Accesso {c.access_period_days || 365}g
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-mono font-bold text-[#0B192C]">€ {Number(c.price).toFixed(0)}</p>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-[#E5E1DA] flex items-center justify-between text-xs">
                    <span className="text-[#5A6472]">
                      <Users size={12} className="inline mr-1" /> {accessCount} attivi
                    </span>
                    <span className="text-[#0B7C5C] font-mono">
                      Revenue potenziale: € {revenue.toFixed(2)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-[0.15em] border ${
        active
          ? "bg-[#0B192C] text-[#F9F8F6] border-[#0B192C]"
          : "bg-white text-[#0B192C] border-[#E5E1DA]"
      }`}
    >
      {children}
    </button>
  );
}
